import type { ReactNode } from 'react'
import { Float } from '@headlessui-float/react'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import type { LocaleEnum } from '@systemeio/ui-frontend'
import { localeNames } from '@systemeio/ui-frontend'
import { useTranslation } from 'next-i18next'
import { useHeadlessUiAnchor } from 'shared/feature-flags/use-headless-ui-anchor'
import { useFloatHover } from 'shared/hooks/use-float-hover'
import { useLanguageSwitcher } from 'shared/hooks/use-language-switcher'
import ChevronDownIcon from 'shared/icons/chevron-down-icon'
import { getCalculatedLanguage } from 'shared/utils/get-calculated-language'

const PublicLanguageSwitcher = ({ mobile }: { mobile?: boolean }): ReactNode => {
  const { i18n } = useTranslation()
  const { show, delayClose, delayOpen, close, open } = useFloatHover()
  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()
  const useNativeAnchor = useHeadlessUiAnchor()

  if (!i18n) {
    return <div>loading...</div>
  }

  const trigger = (
    <PopoverButton
      data-testid="templates-public-language-switcher-trigger"
      onMouseEnter={mobile ? () => {} : delayOpen}
      onMouseLeave={mobile ? () => {} : delayClose}
      onClick={mobile ? (show ? close : open) : () => {}}
      disabled={isChangingLanguage}
      className={`flex size-fit items-center justify-around gap-2.5 focus:outline-hidden ${isChangingLanguage && 'animate-pulse'}`}
    >
      <div className="w-5 text-sm font-bold text-brand-primary">
        {getCalculatedLanguage(i18n?.language)}
      </div>
      <ChevronDownIcon />
    </PopoverButton>
  )

  const panel = (
    <PopoverPanel
      data-testid="templates-public-language-switcher-panel"
      {...(useNativeAnchor
        ? { static: true, portal: true, anchor: { to: 'bottom' as const, gap: 10 } }
        : {})}
      onMouseEnter={mobile ? () => {} : delayOpen}
      onMouseLeave={mobile ? () => {} : delayClose}
      className={`flex flex-col rounded-lg bg-background-secondary text-sm shadow-lg ${isChangingLanguage ? 'pointer-events-none' : ''}`}
    >
      {!useNativeAnchor && (
        <Float.Arrow className={`absolute size-4 rotate-45 bg-background-secondary`} />
      )}
      {useNativeAnchor && <div className={`absolute size-4 rotate-45 bg-background-secondary`} />}
      <ul className={'flex flex-col py-4 text-sm whitespace-nowrap'}>
        {Object.keys(localeNames).map(locale => (
          <li key={locale}>
            <button
              className={`w-full cursor-pointer px-7 py-1 text-start main-transition-colors hover:text-brand-primary focus:outline-hidden focus-visible:text-brand-primary ${
                locale === i18n.language ? 'text-brand-primary' : `text-text-tertiary`
              }`}
            >
              <p
                onClick={() => {
                  close()
                  handleChangeLanguage(locale as LocaleEnum)
                }}
              >
                {localeNames[locale as LocaleEnum]}
              </p>
            </button>
          </li>
        ))}
      </ul>
    </PopoverPanel>
  )

  return (
    <Popover className={'h-full font-mulish'}>
      {useNativeAnchor ? (
        <div className={'flex h-full items-center'}>
          {trigger}
          {show ? panel : null}
        </div>
      ) : (
        <Float
          show={show}
          offset={10}
          portal
          autoPlacement={{ allowedPlacements: ['bottom', 'bottom-end', 'bottom-end', 'top'] }}
          arrow
          enter="transition duration-100 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-75 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className={'flex h-full items-center'}
        >
          {trigger}
          {panel}
        </Float>
      )}
    </Popover>
  )
}

export default PublicLanguageSwitcher
